import { fromUnixTime } from 'date-fns'
import types from './types'
import router from '@/router'
import { Axios, setAxiosHeaders } from '@/helpers/axios'

// As soon as a route is hit, we ensure the Authorisation header is set on the axios instance,
// otherwise sometimes it makes the request prior to checking for a token, which results in errors
const setAxiosToken = (ctx, token) => setAxiosHeaders(token)

const checkAuthentication = async ({ commit }) => {
    const token = localStorage.getItem('acemToken') || sessionStorage.getItem('acemToken')
    const userData = localStorage.getItem('acemUser') || sessionStorage.getItem('acemUser')

    const clearData = () => {
        sessionStorage.clear()
        localStorage.removeItem('acemUser')
        localStorage.removeItem('acemToken')
    }

    if (token && userData) {
        const expiresIn = new Date(JSON.parse(token)['expires_in'])

        if (expiresIn < new Date()) {
            clearData()
        } else {
            const config = {
                headers: { Authorization: `Bearer ${JSON.parse(token).token}` }
            }

            // if the user has reloaded or come back, we need to re-issue them a new token with a new expiry time
            // to ensure they don't randomly get logged out
            await Axios.get('/validate', config)
            .then(({ data }) => {
                // if not valid, just log them out...
                if (data.status !== 'success') throw new Error('Token is not valid or expired')

                // we need to replace the existing data with the new token and expiry
                const localData = {
                    expires_in: new Date(Date.now() + data.exp * 1000),
                    token: data.token
                }

                if (localStorage.getItem('acemToken')) localStorage.setItem('acemToken', localData)
                commit(types.SET_JWT_TOKEN, data.token)
            })
            .catch(err => {
                console.log(err)
                clearData()
            })

            // set the VUEX user data back again.
            commit(types.SET_USER, JSON.parse(userData))
        }

        return
    }

    clearData()
}

const loginUser = ({ dispatch }, { email, password, remember }) => {
    return new Promise((resolve, reject) => {
        Axios.post('/login', { user: { email, password } })
            .then(({ data: { status, profile, response, token, exp } }) => {
                if (status === 'fail') throw new Error(response)

                const { name, email } = profile
                const domain = 'https://acem.w3.digital/'

                const userData = {
                    token,
                    expires_in: exp,
                    user: {
                        name,
                        email,
                        remember,
                        profession: profile[`${domain}user_metadata`].profession || 'Not Specified',
                        role: profile[`${domain}app_metadata`].role || "User"
                    }
                }

                dispatch('setAuthData', userData)
                resolve(userData)
            })
            .catch(err => {
                console.log(err.message || err)
                reject(err)
            })
    })
}

const setAuthData = ({ commit }, { user, ...data }) => {
    setAxiosHeaders(data.token)

    // if null, we need to set an expire in by default, or it'll log the user out on refresh
    if (!data.expires_in || data.expires_in === null) data.expires_in = new Date(Date.now() + data.expires_in * 1000)

    // We convert the expiry into an actual date, so when the user comes back, we compare with the current time
    data.expires_in = fromUnixTime(data.expires_in)

    // If remember me is not selected, they may be on a shared computer, so we clear the data
    // once they close the tab
    if (data.remember) {
        localStorage.setItem('acemToken', JSON.stringify(data))
        localStorage.setItem('acemUser', JSON.stringify(user))
    } else {
        sessionStorage.setItem('acemToken', JSON.stringify(data))
        sessionStorage.setItem('acemUser', JSON.stringify(user))
    }

    commit(types.SET_JWT_TOKEN, data.token)
    commit(types.SET_USER, user)
}

const logoutUser = ({ commit }) => {
    localStorage.removeItem('acemUser')
    localStorage.removeItem('acemToken')
    sessionStorage.removeItem('acemUser')
    sessionStorage.removeItem('acemToken')

    commit(types.LOGOUT_USER)
    router.push('/login')

}

export default {
    setAxiosToken,
    checkAuthentication,
    loginUser,
    setAuthData,
    logoutUser
}
